import { T, useTranslate } from '@tolgee/react';
import { Col, Row } from 'antd';
import Link from 'next/link';
import { TwitterIcon } from 'react-share';
import styled, { css } from 'styled-components';

import Facebook from '../../assets/icons/Facebook';
import Linkedin from '../../assets/icons/Linkedin';
import Logo from '../../assets/icons/Logo';
import BGroundSvg from '../../assets/images/LandingPage/bground.svg';
import theme from '../../assets/theme';
import { useBreakPoint } from '../../hooks/useBreakPoint';
import { useIsMobile } from '../../hooks/useIsMobile';
import { IconWrapper } from '../_atoms/IconWrapper';
import { caseStudies } from '../layouts/NewAppLayout';

export const NewLandingPageFooter = ({
  footerBackgroundColor,
}: {
  footerBackgroundColor?: string;
}) => {
  const ismobile = useIsMobile(); // ? 'true' : undefined;
  const isLargerThan800 = useBreakPoint(800);
  const { t } = useTranslate();
  return (
    <Container
      $footerBackgroundColor={footerBackgroundColor}
      style={{ marginBottom: !isLargerThan800 ? '60px' : '' }}
    >
      <Row
        gutter={[0, 32]}
        style={{
          paddingTop: '80px',
          paddingBottom: '80px',
          width: '100%',
          maxWidth: '1128px',
          marginBottom: 0,
          marginLeft: 'auto',
          marginRight: 'auto',
          color: theme.colors.black,
          position: 'relative',
        }}
        justify={ismobile ? 'start' : 'center'}
      >
        <MadeWithLoveContainer>
          <span style={{ fontWeight: 'bold' }}>
            <T keyName="MadeWithLoveFromNorway" defaultValue="Made with 🧡 from Norway" />
          </span>
        </MadeWithLoveContainer>
        <Col xs={12} md={5} style={{}}>
          <Link href={'/'} passHref prefetch={false}>
            <a>
              <IconWrapper
                color={theme.colors.orange}
                height={'22px'}
                width={'81px'}
                style={{ justifyContent: 'flex-start', display: 'flex' }}
              >
                <Logo />
              </IconWrapper>
            </a>
          </Link>
          <p style={{ color: theme.colors.black, fontWeight: 500 }}>
            <T keyName="Copyright" defaultValue="© 2025 Curipod AS" />
          </p>
          <SocialContainer>
            <span style={{ color: theme.colors.black, fontWeight: 500 }}>
              <T keyName="Follow us" defaultValue="Follow us" />
            </span>
            <Link
              href="https://twitter.com/curipodofficial"
              passHref
              aria-label={t('Twitter link', 'Twitter link')}
              prefetch={false}
            >
              <a
                style={{ color: theme.colors.black }}
                target="__blank"
                rel="noopener noreferrer"
              >
                <TwitterIcon style={{ borderRadius: 50 }} />
              </a>
            </Link>
            <Link
              href="https://www.facebook.com/curipod"
              passHref
              aria-label={t('Facebook link', 'Facebook link')}
              prefetch={false}
            >
              <a
                style={{ color: theme.colors.black }}
                target="__blank"
                rel="noopener noreferrer"
              >
                <Facebook />
              </a>
            </Link>
            <Link
              href="https://www.linkedin.com/company/curipod/"
              passHref
              aria-label={t('Linkedin link', 'Linkedin link')}
              prefetch={false}
            >
              <a
                style={{ color: theme.colors.black }}
                target="__blank"
                rel="noopener noreferrer"
              >
                <Linkedin />
              </a>
            </Link>
          </SocialContainer>
        </Col>
        <Col xs={12} md={4} style={{}}>
          <h3 style={{ color: theme.colors.black, fontWeight: 'bold' }}>
            <T keyName="Company" defaultValue="Company" />
          </h3>
          <Link href="/c/about-us" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="About Us" defaultValue="About Us" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/blogs" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Blog" defaultValue="Blog" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/contact-us" passHref prefetch={false}>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Contact Us" defaultValue="Contact Us" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/pricing" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Pricing" defaultValue="Pricing" />
              </StyledLink>
            </a>
          </Link>
          <Link href="https://curipod.com/c/careers" passHref prefetch={false}>
            <a target="__blank" rel="noopener noreferrer">
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Careers" defaultValue="Careers" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/learning-principles" passHref prefetch={false}>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T
                  keyName="The Curipod Learning Principles"
                  defaultValue="The Curipod Learning Principles"
                />
              </StyledLink>
            </a>
          </Link>
        </Col>
        <Col xs={12} md={4} style={{}}>
          <h3 style={{ color: theme.colors.black, fontWeight: 'bold' }}>
            <T keyName="Community" defaultValue="Community" />
          </h3>
          <Link href="/community" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Join Our Community" defaultValue="Join Our Community" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/ambassadors" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Ambassador Program" defaultValue="Ambassador Program" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/events" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Webinars and Events" defaultValue="Webinars and Events" />
              </StyledLink>
            </a>
          </Link>
        </Col>
        <Col xs={12} md={4} style={{}}>
          <h3 style={{ color: theme.colors.black, fontWeight: 'bold' }}>
            <T keyName="Legal" defaultValue="Legal" />
          </h3>
          <Link href="/privacy-policy" passHref prefetch={false}>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Privacy Policy" defaultValue="Privacy Policy" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/state-and-district-privacy-agreements" passHref prefetch={false}>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T
                  keyName="State and District Privacy Agreements"
                  defaultValue="State and District Privacy Agreements"
                />
              </StyledLink>
            </a>
          </Link>
          <Link href="/cookie-policy" passHref prefetch={false}>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Cookie Policy" defaultValue="Cookie Policy" />
              </StyledLink>
            </a>
          </Link>
          <Link href="https://curipod.com/c/safe-ai-in-curipod" passHref prefetch={false}>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="AI in Curipod" defaultValue="AI in Curipod" />
              </StyledLink>
            </a>
          </Link>

          <Link href="/terms-of-use" passHref prefetch={false}>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Terms of Use" defaultValue="Terms of Use" />
              </StyledLink>
            </a>
          </Link>
        </Col>

        <Col xs={12} md={7} style={{}}>
          <h3 style={{ color: theme.colors.black, fontWeight: 'bold' }}>
            <T keyName="Resources" defaultValue="Resources" />
          </h3>
          {caseStudies.map(study => (
            <Link key={study.link} href={study.link} passHref prefetch={false}>
              <a target="__blank" rel="noopener noreferrer">
                <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                  {study.text}
                </StyledLink>
              </a>
            </Link>
          ))}
          <Link href={`/c/courses`} passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Curipod Courses" defaultValue="Curipod Courses" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/staar-test-prep" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T
                  keyName="STAAR & TELPAS Test Prep"
                  defaultValue="STAAR & TELPAS Test Prep"
                />
              </StyledLink>
            </a>
          </Link>

          <Link href="/business" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="For Business" defaultValue="For Business" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/accessibility" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Accessibility" defaultValue="Accessibility" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/how-to-moderate-inappropriate-students-in-curipod" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Moderation Tool" defaultValue="Moderation Tool" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/connectivity-issues" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Connectivity Issues" defaultValue="Connectivity Issues" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/training-and-professional-development" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Training and PD" defaultValue="Training and PD" />
              </StyledLink>
            </a>
          </Link>
          <Link href="/c/curipod-coaches" passHref>
            <a>
              <StyledLink $color={theme.colors.black} style={{ display: 'block' }}>
                <T keyName="Curipod Coaches" defaultValue="Curipod Coaches" />
              </StyledLink>
            </a>
          </Link>
        </Col>
      </Row>
    </Container>
  );
};

const StyledLink = styled.span<{ $color?: string }>`
  color: ${({ $color }) => ($color ? $color : theme.colors.white)};
  font-weight: bold;
  :hover {
    color: ${theme.colors.black};
  }
`;

const MadeWithLoveContainer = styled.div`
  position: absolute;
  bottom: 10px;
  right: 90px;
`;

const Container = styled.div<{ $footerBackgroundColor?: string }>`
  width: 100%;

  ${({ $footerBackgroundColor }) =>
    $footerBackgroundColor
      ? css`
          background-color: ${$footerBackgroundColor};
        `
      : css`
          background-image: url(${BGroundSvg.src});
          background-size: cover;
        `}
  @media (max-width: 1128px) {
    padding: 0px 20px;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  p,
  a,
  span {
    font-family: Nunito;
  }

  p {
    font-weight: bold;
    line-height: 22px;
    font-size: 14px;
    margin-bottom: 10px;
  }
  a {
    text-decoration: unset;
    line-height: 20px;
  }
  h3 {
    font-size: 1.17em;
    line-height: 1.5715;
    margin-bottom: 0.5em;
    margin-top: 0;
  }
`;

const SocialContainer = styled.div`
  display: flex;
  align-items: center;
  & > * {
    margin-right: 5px;
  }
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
  }
  svg {
    height: 18px;
    width: 18px;
  }
`;
